import { useState } from 'react'

import { colors } from 'theme'

import { type User } from 'helpers/interfaces'

import Avatar from '@mui/material/Avatar'
import ButtonBase from '@mui/material/ButtonBase'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'

import { ProfilePopoverContent } from './ProfilePopoverContent'

type ProfileProps = {
  user: User
}

export const Profile = ({ user }: ProfileProps) => {
  const fullName = `${user.firstName} ${user.lastName}`
  const initials = `${user.firstName[0]}${user.lastName[0]}`

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const onClose = () => setAnchorEl(null)

  return (
    <>
      <ButtonBase
        sx={(theme) => ({
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          // Custom ripple effect that only overlaps the Avatar
          '& .MuiTouchRipple-root': {
            width: 30,
            borderRadius: '50%',
          },
          mt: 2,
          color: theme.palette.primary.main,
          '& .MuiTouchRipple-root .MuiTouchRipple-rippleVisible': {
            color: theme.palette.primary.main,
          },
          '&.Mui-focusVisible': {
            backgroundColor: theme.palette.grey[300],
          },
          // Show a custom underline on hover
          ':hover': {
            '& > .MuiTypography-root:nth-of-type(1)': {
              '::after': {
                width: 'calc(100% - 8px)', // Make sure the underline is the same width as the text - take into account the left padding
              },
            },
          },
        })}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Avatar alt={fullName} sx={{ width: 30, height: 30, backgroundColor: colors.primaryMain }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{initials}</Typography>
        </Avatar>
        <Typography
          sx={(theme) => ({
            pl: 1,
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '26px',
            letterSpacing: '0.46px',
            position: 'relative',
            '::after': {
              background: colors.tabsColorUnderline,
              position: 'absolute',
              height: 5,
              width: 0, // Hide the underline when not hovered
              bottom: 0,
              left: '50%',
              transform: 'translateX(calc(-50% + 4px))', // Make sure it's centred below the text - take into account the left padding
              content: '""',
              pointerEvents: 'none',
              borderRadius: theme.border.radius(),
            },
          })}
        >
          {fullName}
        </Typography>
      </ButtonBase>

      <Popover
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        anchorEl={anchorEl}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
        }}
        open={Boolean(anchorEl)}
      >
        <ProfilePopoverContent user={user} />
      </Popover>
    </>
  )
}
